import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AtSign, Loader2, Plus, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mail`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Admin → Users: which shared Workspace mailboxes (AP@, admin@, sales@ …) each office user may open in Mail.
export function MailboxAccessPanel() {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState({});
  const [busy, setBusy] = useState('');
  const load = () => axios.get(`${API}/mailbox-access`, auth()).then(r => setRows(r.data.users)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows) return null;

  const save = async (u, boxes) => {
    setBusy(u.id);
    try {
      const r = await axios.put(`${API}/mailbox-access/${u.id}`, { mailboxes: boxes }, auth());
      setRows(rs => rs.map(x => (x.id === u.id ? { ...x, extra_mailboxes: r.data.extra_mailboxes } : x)));
      setAdding(a => ({ ...a, [u.id]: '' }));
      toast.success(`${u.full_name} can open: ${['their own', ...r.data.extra_mailboxes].join(', ')}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setBusy(''); }
  };
  const withAccess = rows.filter(u => u.extra_mailboxes.length);
  const others = rows.filter(u => !u.extra_mailboxes.length);

  const Row = ({ u }) => (
    <div className="flex flex-wrap items-center gap-2 py-2 border-b border-slate-100 last:border-0" data-testid={`mailbox-access-row-${u.id}`}>
      <div className="min-w-[180px]">
        <p className="text-sm font-semibold text-slate-900">{u.full_name}</p>
        <p className="text-[11px] text-slate-500">{u.email}</p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {u.extra_mailboxes.map(m => (
          <span key={m} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs font-semibold pl-2.5 pr-1 py-0.5" data-testid={`mailbox-chip-${u.id}-${m.split('@')[0]}`}>
            {m}
            <button onClick={() => save(u, u.extra_mailboxes.filter(x => x !== m))} disabled={busy === u.id} className="rounded-full p-0.5 hover:bg-emerald-100" title={`Remove ${m}`} data-testid={`mailbox-remove-${u.id}-${m.split('@')[0]}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
        <form className="inline-flex items-center gap-1" onSubmit={e => { e.preventDefault(); const v = (adding[u.id] || '').trim(); if (v) save(u, [...u.extra_mailboxes, v]); }}>
          <input value={adding[u.id] || ''} onChange={e => setAdding(a => ({ ...a, [u.id]: e.target.value }))} placeholder="ap@365isi.com"
            className="h-7 w-44 px-2 rounded-lg border border-slate-300 text-xs text-slate-900" data-testid={`mailbox-add-input-${u.id}`} />
          <button type="submit" disabled={busy === u.id || !(adding[u.id] || '').trim()} className="h-7 px-2 rounded-lg bg-slate-900 text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-40" data-testid={`mailbox-add-btn-${u.id}`}>
            {busy === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mt-4" data-testid="mailbox-access-panel">
      <p className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5"><AtSign className="w-4 h-4" /> Shared mailboxes in Mail</p>
      <p className="text-[11px] text-slate-500 mb-2">Give an office user extra Workspace mailboxes (AP@, admin@, sales@ …). They show up as buttons under the Mail title; replies go out from that address. Must be a real user mailbox Ops can open — groups and aliases won't work.</p>
      {withAccess.map(u => <Row key={u.id} u={u} />)}
      {others.length > 0 && (
        <details className="mt-2">
          <summary className="text-xs font-semibold text-slate-600 cursor-pointer" data-testid="mailbox-access-others">Everyone else who uses Mail ({others.length})</summary>
          {others.map(u => <Row key={u.id} u={u} />)}
        </details>
      )}
    </div>
  );
}
